import { Component, computed, inject, input } from '@angular/core';
import { ResponsiveService } from '../services/responsive';
import { computeVisibleCriteria, ScoreCriterion } from '../services/api/fundamental-scores';

// One icon per category key, reused across systems where the same key/concept recurs
// (e.g. 'red_flags' appears on both growth and dividend). Best-effort mapping onto the
// 7 icons provided — not a confirmed 1:1 Figma spec, adjust if a mapping looks wrong.
const CATEGORY_ICON: Record<string, string> = {
  growth_stability: '/v3/chartUp.svg',
  growth: '/v3/chartUp.svg',
  dividend_growth: '/v3/chartUp.svg',
  performance: '/v3/chartUp.svg',
  trend: '/v3/chart.svg',
  financeability: '/v3/chart.svg',
  profitability: '/v3/profitability.svg',
  dividend_yield: '/v3/profitability.svg',
  price_volatility: '/v3/chartDown.svg',
  volatility: '/v3/chartDown.svg',
  drawdown: '/v3/chartDown.svg',
  safety_balance: '/v3/shield.svg',
  company_quality: '/v3/shield.svg',
  downside_robustness: '/v3/shield.svg',
  dividend_continuity: '/v3/calendar.svg',
  red_flags: '/v3/redFlag.svg',
};

// V3's counterpart to V2's FundamentalCategoryCard — always expanded (no toggle), a card
// only on mobile (desktop renders flush, no box — see V3FundamentalScoresAnalysis), with a
// header icon (per category key) and a per-criterion pill row instead of a text subtitle.
@Component({
  selector: 'app-v3-category-card',
  imports: [],
  standalone: true,
  templateUrl: './v3-category-card.html',
  styleUrl: './v3-category-card.scss',
})
export class V3CategoryCard {
  readonly key = input.required<string>();
  readonly title = input.required<string>();
  readonly criteria = input.required<ScoreCriterion[]>();
  /** "Red Flags?" card — a fulfilled criterion is bad, so pill/dot colors invert. */
  readonly isRedFlag = input(false);

  private readonly responsiveService = inject(ResponsiveService);
  readonly isMobile = this.responsiveService.isMobile;

  readonly icon = computed(() => CATEGORY_ICON[this.key()] ?? '/v3/chart.svg');
  readonly displayTitle = computed(() => (this.isRedFlag() ? 'Red Flags' : this.title()));

  readonly visibleCriteria = computed(() =>
    computeVisibleCriteria(this.criteria(), this.isRedFlag()),
  );

  // Every visible red-flag criterion is, by definition, a present (bad) flag — "fulfilled/
  // total" would always read "0/N", which looks like nothing passed rather than "N flags
  // found". Show a plain flag count there instead of a fraction.
  readonly scoreText = computed(() => {
    const visible = this.visibleCriteria();
    if (this.isRedFlag()) return `${visible.length}`;
    const fulfilled = visible.filter((criterion) => this.isCriterionGood(criterion)).length;
    return `${fulfilled}/${visible.length}`;
  });

  isCriterionGood(criterion: ScoreCriterion): boolean {
    return !this.isRedFlag() && criterion.fulfilled;
  }

  // Abridged for V3 — "besser als {n} %" instead of V2's full sentence (criterionDescription
  // in services/api/fundamental-scores.ts, which stays unchanged for V2's own use).
  percentileText(criterion: ScoreCriterion): string | null {
    return criterion.percentile != null ? `besser als ${criterion.percentile} %` : null;
  }
}
